import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

// Breathing pattern configuration
interface BreathPattern {
  name: string;
  inhale: number;
  holdIn: number;
  exhale: number;
  holdOut: number;
}

// Sound type configuration
type SoundType = 'harmonic' | 'ocean' | 'binaural' | 'silent';

interface SoundConfig {
  type: SoundType;
  name: string;
  nameEn: string;
  description: string;
  descriptionEn: string;
}

// Phase configuration for visual display
interface PhaseConfig {
  color: string;
  gradient: string;
  icon: 'up' | 'pause' | 'down';
  labelNo: string;
  labelEn: string;
}

const PHASE_CONFIG: Record<string, PhaseConfig> = {
  inhale: {
    color: '#34d399',
    gradient: 'from-emerald-400 to-teal-400',
    icon: 'up',
    labelNo: 'Pust inn',
    labelEn: 'Breathe in'
  },
  holdIn: {
    color: '#22d3ee',
    gradient: 'from-cyan-400 to-sky-400',
    icon: 'pause',
    labelNo: 'Hold',
    labelEn: 'Hold'
  },
  exhale: {
    color: '#818cf8',
    gradient: 'from-indigo-400 to-violet-400',
    icon: 'down',
    labelNo: 'Pust ut',
    labelEn: 'Breathe out'
  },
  holdOut: {
    color: '#c084fc',
    gradient: 'from-purple-400 to-fuchsia-400',
    icon: 'pause',
    labelNo: 'Hold',
    labelEn: 'Hold'
  },
  idle: {
    color: '#a8a29e',
    gradient: 'from-stone-400 to-stone-500',
    icon: 'pause',
    labelNo: 'Klar',
    labelEn: 'Ready'
  }
};

const PATTERNS: Record<string, BreathPattern> = {
  relaxation: { name: 'Relax', inhale: 6, holdIn: 2, exhale: 6, holdOut: 2 },
  box: { name: 'Box', inhale: 4, holdIn: 4, exhale: 4, holdOut: 4 },
  sleep478: { name: '4-7-8', inhale: 4, holdIn: 7, exhale: 8, holdOut: 0 },
  coherence: { name: 'Coherence', inhale: 5, holdIn: 0, exhale: 5, holdOut: 0 },
  deep: { name: 'Deep', inhale: 6, holdIn: 3, exhale: 12, holdOut: 3 },
};

const SOUND_TYPES: Record<SoundType, SoundConfig> = {
  harmonic: {
    type: 'harmonic',
    name: 'Harmonisk',
    nameEn: 'Harmonic',
    description: 'Akkorder som følger pusten',
    descriptionEn: 'Chords follow breath'
  },
  ocean: {
    type: 'ocean',
    name: 'Hav',
    nameEn: 'Ocean',
    description: 'Bølger inn og ut',
    descriptionEn: 'Waves in and out'
  },
  binaural: {
    type: 'binaural',
    name: 'Binaural',
    nameEn: 'Binaural',
    description: '432Hz + 6Hz theta',
    descriptionEn: '432Hz + 6Hz theta'
  },
  silent: {
    type: 'silent',
    name: 'Stille',
    nameEn: 'Silent',
    description: 'Kun visuell',
    descriptionEn: 'Visual only'
  },
};

type BreathPhase = 'inhale' | 'holdIn' | 'exhale' | 'holdOut' | 'idle';

interface BreathingExerciseProps {
  onComplete?: () => void;
  duration?: number;
}

// Smooth easing functions
const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;
const easeOutQuad = (t: number): number => 1 - (1 - t) * (1 - t);
const easeInQuad = (t: number): number => t * t;

// Storage keys
const STORAGE_KEYS = {
  pattern: 'breathe_pattern',
  soundType: 'breathe_soundType',
  soundEnabled: 'breathe_soundEnabled',
};

// Musical notes (frequencies in Hz)
const NOTES = {
  C3: 130.81,
  E3: 164.81,
  G3: 196.00,
  C4: 261.63,
  E4: 329.63,
  G4: 392.00,
  C5: 523.25,
};

const BreathingExercise: React.FC<BreathingExerciseProps> = ({
  onComplete,
  duration = 5
}) => {
  const { t, language } = useLanguage();

  // Load saved preferences
  const loadSaved = <T,>(key: string, defaultValue: T): T => {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // State
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState<BreathPhase>('idle');
  const [phaseTime, setPhaseTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [cycleCount, setCycleCount] = useState(0);
  const [selectedPattern, setSelectedPattern] = useState<string>(() => loadSaved(STORAGE_KEYS.pattern, 'relaxation'));
  const [soundType, setSoundType] = useState<SoundType>(() => loadSaved(STORAGE_KEYS.soundType, 'harmonic'));
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => loadSaved(STORAGE_KEYS.soundEnabled, true));

  // Save preferences when changed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.pattern, JSON.stringify(selectedPattern));
  }, [selectedPattern]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.soundType, JSON.stringify(soundType));
  }, [soundType]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.soundEnabled, JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  // Audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const nodesRef = useRef<{
    // Harmonic oscillators (chord)
    oscRoot?: OscillatorNode;
    oscThird?: OscillatorNode;
    oscFifth?: OscillatorNode;
    oscOctave?: OscillatorNode;
    harmonicGain?: GainNode;
    harmonicFilter?: BiquadFilterNode;
    // Binaural
    leftOsc?: OscillatorNode;
    rightOsc?: OscillatorNode;
    binauralGain?: GainNode;
    // Ocean
    noiseSource?: AudioBufferSourceNode;
    noiseGain?: GainNode;
    noiseFilter?: BiquadFilterNode;
    // Master
    masterGain?: GainNode;
  }>({});

  // Animation ref
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const waveAnimRef = useRef<number>(0);

  const pattern = PATTERNS[selectedPattern];
  const sessionDuration = duration * 60;

  // Create ocean noise buffer
  const createNoiseBuffer = useCallback((ctx: AudioContext): AudioBuffer => {
    const bufferSize = ctx.sampleRate * 4;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }, []);

  // Initialize Web Audio based on sound type
  const initAudio = useCallback(() => {
    if (audioContextRef.current || !soundEnabled || soundType === 'silent') return;

    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    audioContextRef.current = ctx;

    // Master gain
    const masterGain = ctx.createGain();
    masterGain.gain.value = 0.5;
    masterGain.connect(ctx.destination);
    nodesRef.current.masterGain = masterGain;

    if (soundType === 'harmonic') {
      // Create harmonic chord that follows breath
      // Low pass filter for warmth and control
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 300; // Start muffled
      filter.Q.value = 0.7;
      filter.connect(masterGain);
      nodesRef.current.harmonicFilter = filter;

      // Gain for harmonics
      const harmonicGain = ctx.createGain();
      harmonicGain.gain.value = 0;
      harmonicGain.connect(filter);
      nodesRef.current.harmonicGain = harmonicGain;

      // Root note (C3)
      const oscRoot = ctx.createOscillator();
      oscRoot.type = 'sine';
      oscRoot.frequency.value = NOTES.C3;
      oscRoot.connect(harmonicGain);
      oscRoot.start();
      nodesRef.current.oscRoot = oscRoot;

      // Third (E3) - softer
      const thirdGain = ctx.createGain();
      thirdGain.gain.value = 0.6;
      thirdGain.connect(harmonicGain);
      const oscThird = ctx.createOscillator();
      oscThird.type = 'sine';
      oscThird.frequency.value = NOTES.E3;
      oscThird.connect(thirdGain);
      oscThird.start();
      nodesRef.current.oscThird = oscThird;

      // Fifth (G3)
      const fifthGain = ctx.createGain();
      fifthGain.gain.value = 0.5;
      fifthGain.connect(harmonicGain);
      const oscFifth = ctx.createOscillator();
      oscFifth.type = 'sine';
      oscFifth.frequency.value = NOTES.G3;
      oscFifth.connect(fifthGain);
      oscFifth.start();
      nodesRef.current.oscFifth = oscFifth;

      // Octave (C4) - adds brightness at peak
      const octaveGain = ctx.createGain();
      octaveGain.gain.value = 0.3;
      octaveGain.connect(harmonicGain);
      const oscOctave = ctx.createOscillator();
      oscOctave.type = 'sine';
      oscOctave.frequency.value = NOTES.C4;
      oscOctave.connect(octaveGain);
      oscOctave.start();
      nodesRef.current.oscOctave = oscOctave;
    }

    if (soundType === 'binaural') {
      // Binaural beats setup (432Hz carrier + 6Hz theta)
      const binauralGain = ctx.createGain();
      binauralGain.gain.value = 0.15;
      binauralGain.connect(masterGain);
      nodesRef.current.binauralGain = binauralGain;

      const merger = ctx.createChannelMerger(2);
      merger.connect(binauralGain);

      const leftOsc = ctx.createOscillator();
      leftOsc.type = 'sine';
      leftOsc.frequency.value = 432;
      leftOsc.connect(merger, 0, 0);
      leftOsc.start();
      nodesRef.current.leftOsc = leftOsc;

      const rightOsc = ctx.createOscillator();
      rightOsc.type = 'sine';
      rightOsc.frequency.value = 438; // 432 + 6Hz theta
      rightOsc.connect(merger, 0, 1);
      rightOsc.start();
      nodesRef.current.rightOsc = rightOsc;
    }

    if (soundType === 'ocean') {
      // Ocean waves using filtered noise
      const noiseGain = ctx.createGain();
      noiseGain.gain.value = 0;
      nodesRef.current.noiseGain = noiseGain;

      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = 200;
      filter.Q.value = 1;
      filter.connect(noiseGain);
      nodesRef.current.noiseFilter = filter;

      noiseGain.connect(masterGain);

      const noiseBuffer = createNoiseBuffer(ctx);
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;
      noise.loop = true;
      noise.connect(filter);
      noise.start();
      nodesRef.current.noiseSource = noise;
    }

    // Media Session API
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Pusteøvelse',
        artist: 'Breathe to Become',
        album: 'Meditasjon',
      });
    }
  }, [soundEnabled, soundType, createNoiseBuffer]);

  // Update audio based on breath phase - DIRECTLY SYNCHRONIZED
  const updateAudioForPhase = useCallback((currentPhase: BreathPhase, progress: number) => {
    if (!soundEnabled || soundType === 'silent') return;

    const ctx = audioContextRef.current;
    if (!ctx) return;

    const now = ctx.currentTime;
    const nodes = nodesRef.current;
    const rampTime = 0.05; // Very short ramp for tight sync

    // HARMONIC SOUND - Direct volume/filter control matching breath
    if (soundType === 'harmonic' && nodes.harmonicGain && nodes.harmonicFilter) {
      const gain = nodes.harmonicGain.gain;
      const filter = nodes.harmonicFilter.frequency;

      // Cancel any scheduled changes for immediate response
      gain.cancelScheduledValues(now);
      filter.cancelScheduledValues(now);

      switch (currentPhase) {
        case 'inhale': {
          // Volume rises from 0 to peak as we inhale
          const volume = 0.05 + (0.55 * progress);
          const filterFreq = 200 + (1300 * progress);
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(volume, now + rampTime);
          filter.setValueAtTime(filter.value, now);
          filter.linearRampToValueAtTime(filterFreq, now + rampTime);
          break;
        }
        case 'holdIn': {
          // Sustained at peak with gentle variation
          const holdVolume = 0.55 + Math.sin(progress * Math.PI * 2) * 0.05;
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(holdVolume, now + rampTime);
          filter.setValueAtTime(filter.value, now);
          filter.linearRampToValueAtTime(1500, now + rampTime);
          break;
        }
        case 'exhale': {
          // Volume falls from peak to near-silence
          const volume = 0.6 - (0.55 * progress);
          const filterFreq = 1500 - (1300 * progress);
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(volume, now + rampTime);
          filter.setValueAtTime(filter.value, now);
          filter.linearRampToValueAtTime(filterFreq, now + rampTime);
          break;
        }
        case 'holdOut': {
          // Nearly silent pause
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(0.03, now + rampTime);
          filter.setValueAtTime(filter.value, now);
          filter.linearRampToValueAtTime(200, now + rampTime);
          break;
        }
      }
    }

    // BINAURAL - Volume follows breath directly
    if (soundType === 'binaural' && nodes.binauralGain) {
      const gain = nodes.binauralGain.gain;
      gain.cancelScheduledValues(now);

      switch (currentPhase) {
        case 'inhale': {
          const volume = 0.08 + (0.27 * progress);
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(volume, now + rampTime);
          break;
        }
        case 'holdIn': {
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(0.35, now + rampTime);
          break;
        }
        case 'exhale': {
          const volume = 0.35 - (0.27 * progress);
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(volume, now + rampTime);
          break;
        }
        case 'holdOut': {
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(0.05, now + rampTime);
          break;
        }
      }
    }

    // OCEAN - Wave sounds directly following breath
    if (soundType === 'ocean' && nodes.noiseGain && nodes.noiseFilter) {
      const gain = nodes.noiseGain.gain;
      const filter = nodes.noiseFilter.frequency;

      gain.cancelScheduledValues(now);
      filter.cancelScheduledValues(now);

      switch (currentPhase) {
        case 'inhale': {
          // Wave rushing in
          const volume = 0.05 + (0.5 * progress);
          const filterFreq = 150 + (700 * progress);
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(volume, now + rampTime);
          filter.setValueAtTime(filter.value, now);
          filter.linearRampToValueAtTime(filterFreq, now + rampTime);
          break;
        }
        case 'holdIn': {
          // Wave at peak
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(0.55, now + rampTime);
          filter.setValueAtTime(filter.value, now);
          filter.linearRampToValueAtTime(850, now + rampTime);
          break;
        }
        case 'exhale': {
          // Wave receding
          const volume = 0.55 - (0.5 * progress);
          const filterFreq = 850 - (700 * progress);
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(volume, now + rampTime);
          filter.setValueAtTime(filter.value, now);
          filter.linearRampToValueAtTime(filterFreq, now + rampTime);
          break;
        }
        case 'holdOut': {
          // Quiet pause between waves
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(0.03, now + rampTime);
          filter.setValueAtTime(filter.value, now);
          filter.linearRampToValueAtTime(150, now + rampTime);
          break;
        }
      }
    }
  }, [soundEnabled, soundType]);

  // Clean up audio
  const cleanupAudio = useCallback(() => {
    const nodes = nodesRef.current;

    try {
      nodes.oscRoot?.stop();
      nodes.oscThird?.stop();
      nodes.oscFifth?.stop();
      nodes.oscOctave?.stop();
      nodes.leftOsc?.stop();
      nodes.rightOsc?.stop();
      nodes.noiseSource?.stop();
    } catch {}

    nodesRef.current = {};

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  // Main animation/timing loop
  const tick = useCallback((timestamp: number) => {
    if (!isActive) return;

    const deltaTime = lastTimeRef.current ? (timestamp - lastTimeRef.current) / 1000 : 0;
    lastTimeRef.current = timestamp;
    waveAnimRef.current = timestamp;

    setTotalTime(prev => {
      const newTotal = prev + deltaTime;
      if (newTotal >= sessionDuration) {
        stopExercise();
        onComplete?.();
        return sessionDuration;
      }
      return newTotal;
    });

    setPhaseTime(prev => {
      const newTime = prev + deltaTime;

      let currentPhaseDuration = 0;
      switch (phase) {
        case 'inhale': currentPhaseDuration = pattern.inhale; break;
        case 'holdIn': currentPhaseDuration = pattern.holdIn; break;
        case 'exhale': currentPhaseDuration = pattern.exhale; break;
        case 'holdOut': currentPhaseDuration = pattern.holdOut; break;
      }

      if (currentPhaseDuration === 0) {
        // Skip this phase
        switch (phase) {
          case 'holdIn': setPhase('exhale'); break;
          case 'holdOut': setPhase('inhale'); setCycleCount(c => c + 1); break;
        }
        return 0;
      }

      const progress = Math.min(newTime / currentPhaseDuration, 1);
      updateAudioForPhase(phase, progress);

      if (newTime >= currentPhaseDuration) {
        switch (phase) {
          case 'inhale':
            setPhase(pattern.holdIn > 0 ? 'holdIn' : 'exhale');
            break;
          case 'holdIn':
            setPhase('exhale');
            break;
          case 'exhale':
            if (pattern.holdOut > 0) {
              setPhase('holdOut');
            } else {
              setPhase('inhale');
              setCycleCount(c => c + 1);
            }
            break;
          case 'holdOut':
            setPhase('inhale');
            setCycleCount(c => c + 1);
            break;
        }
        return 0;
      }

      return newTime;
    });

    animationRef.current = requestAnimationFrame(tick);
  }, [isActive, phase, pattern, sessionDuration, onComplete, updateAudioForPhase]);

  // Start exercise
  const startExercise = useCallback(() => {
    initAudio();
    setIsActive(true);
    setPhase('inhale');
    setPhaseTime(0);
    setTotalTime(0);
    setCycleCount(0);
    lastTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(tick);
  }, [initAudio, tick]);

  // Stop exercise
  const stopExercise = useCallback(() => {
    setIsActive(false);
    setPhase('idle');
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    cleanupAudio();
  }, [cleanupAudio]);

  useEffect(() => {
    if (isActive) {
      animationRef.current = requestAnimationFrame(tick);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive, tick]);

  useEffect(() => {
    return () => {
      cleanupAudio();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [cleanupAudio]);

  // Calculate breath size (0 = empty lungs, 1 = full lungs)
  const getBreathSize = () => {
    if (!isActive || phase === 'idle') return 0.3;

    switch (phase) {
      case 'inhale':
        return easeInOutSine(phaseTime / pattern.inhale);
      case 'holdIn':
        return 1;
      case 'exhale':
        return 1 - easeInOutSine(phaseTime / pattern.exhale);
      case 'holdOut':
        return 0;
      default:
        return 0.3;
    }
  };

  const breathSize = getBreathSize();

  // Get current phase duration for countdown (must be defined before getPhaseProgress)
  const getCurrentPhaseDuration = () => {
    switch (phase) {
      case 'inhale': return pattern.inhale;
      case 'holdIn': return pattern.holdIn;
      case 'exhale': return pattern.exhale;
      case 'holdOut': return pattern.holdOut;
      default: return 0;
    }
  };

  // Get current phase progress (0 to 1)
  const getPhaseProgress = () => {
    if (!isActive || phase === 'idle') return 0;
    const duration = getCurrentPhaseDuration();
    return duration > 0 ? Math.min(phaseTime / duration, 1) : 0;
  };

  const phaseProgress = getPhaseProgress();
  const phaseRemaining = Math.ceil(getCurrentPhaseDuration() - phaseTime);

  // Get active phases for this pattern (skip zero-duration phases)
  const getActivePhases = () => {
    const phases: BreathPhase[] = ['inhale'];
    if (pattern.holdIn > 0) phases.push('holdIn');
    phases.push('exhale');
    if (pattern.holdOut > 0) phases.push('holdOut');
    return phases;
  };

  const activePhases = getActivePhases();

  // Calculate total cycle duration and segment angles for the ring
  const getTotalCycleDuration = () => {
    return pattern.inhale + pattern.holdIn + pattern.exhale + pattern.holdOut;
  };

  const getPhaseAngles = () => {
    const total = getTotalCycleDuration();
    let startAngle = -90; // Start at top (12 o'clock)
    const angles: Record<string, { start: number; end: number; duration: number }> = {};

    const orderedPhases: BreathPhase[] = ['inhale', 'holdIn', 'exhale', 'holdOut'];
    orderedPhases.forEach(p => {
      const duration = pattern[p as keyof BreathPattern] as number;
      if (duration > 0) {
        const sweepAngle = (duration / total) * 360;
        angles[p] = {
          start: startAngle,
          end: startAngle + sweepAngle,
          duration
        };
        startAngle += sweepAngle;
      }
    });

    return angles;
  };

  const phaseAngles = getPhaseAngles();

  // Get phase config
  const currentPhaseConfig = PHASE_CONFIG[phase] || PHASE_CONFIG.idle;

  const getPhaseLabel = () => {
    const config = PHASE_CONFIG[phase];
    if (!config) return language === 'en' ? 'Ready' : 'Klar';
    return language === 'en' ? config.labelEn : config.labelNo;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // SVG arc path helper
  const describeArc = (cx: number, cy: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M", start.x, start.y,
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");
  };

  const polarToCartesian = (cx: number, cy: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees) * Math.PI / 180.0;
    return {
      x: cx + (radius * Math.cos(angleInRadians)),
      y: cy + (radius * Math.sin(angleInRadians))
    };
  };

  // Get localized sound config
  const getSoundName = (config: SoundConfig) => {
    return language === 'en' ? config.nameEn : config.name;
  };

  const getSoundDescription = (config: SoundConfig) => {
    return language === 'en' ? config.descriptionEn : config.description;
  };

  // Render phase icon
  const renderPhaseIcon = (iconType: 'up' | 'pause' | 'down', size: number = 24) => {
    if (iconType === 'up') {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    } else if (iconType === 'down') {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M12 5v14M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    } else {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </svg>
      );
    }
  };

  // Calculate the visual circle scale (minScale to maxScale based on breath)
  const minScale = 0.4;
  const maxScale = 1.0;
  const circleScale = minScale + (breathSize * (maxScale - minScale));

  return (
    <div className="flex flex-col items-center justify-center min-h-[650px] p-4 sm:p-6">
      {/* Main breathing visualization */}
      <div className="relative w-72 h-72 sm:w-80 sm:h-80 mb-6">

        {/* Outer ring - phase progress indicator */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
          <defs>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={currentPhaseConfig.color} stopOpacity="1" />
              <stop offset="100%" stopColor={currentPhaseConfig.color} stopOpacity="0.6" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          {/* Background circle track */}
          <circle
            cx="100"
            cy="100"
            r="94"
            fill="none"
            stroke="#e7e5e4"
            strokeWidth="6"
          />

          {/* Phase progress arc - shows how far through current phase */}
          {isActive && phase !== 'idle' && (
            <circle
              cx="100"
              cy="100"
              r="94"
              fill="none"
              stroke={currentPhaseConfig.color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${phaseProgress * 590.6} 590.6`}
              transform="rotate(-90 100 100)"
              filter="url(#glow)"
              style={{ transition: 'stroke-dasharray 0.1s linear' }}
            />
          )}
        </svg>

        {/* The main breathing circle - this is what expands/contracts */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Outer glow ring */}
          <div
            className="absolute rounded-full transition-transform duration-100 ease-out"
            style={{
              width: '220px',
              height: '220px',
              transform: `scale(${circleScale})`,
              background: isActive
                ? `radial-gradient(circle, ${currentPhaseConfig.color}15 0%, transparent 70%)`
                : 'radial-gradient(circle, #a8a29e10 0%, transparent 70%)',
            }}
          />

          {/* Main breathing circle */}
          <div
            className="absolute rounded-full transition-transform duration-100 ease-out flex items-center justify-center"
            style={{
              width: '180px',
              height: '180px',
              transform: `scale(${circleScale})`,
              background: isActive
                ? `radial-gradient(circle at 30% 30%, ${currentPhaseConfig.color}90, ${currentPhaseConfig.color}70)`
                : 'radial-gradient(circle at 30% 30%, #d4d4d4, #a8a29e)',
              boxShadow: isActive
                ? `0 0 40px ${currentPhaseConfig.color}50, 0 0 80px ${currentPhaseConfig.color}30`
                : '0 0 20px rgba(0,0,0,0.1)',
            }}
          >
            {/* Inner lighter circle for depth */}
            <div
              className="absolute rounded-full"
              style={{
                width: '140px',
                height: '140px',
                background: isActive
                  ? `radial-gradient(circle at 40% 40%, white, ${currentPhaseConfig.color}40)`
                  : 'radial-gradient(circle at 40% 40%, white, #e7e5e4)',
                opacity: 0.3,
              }}
            />
          </div>
        </div>

        {/* Center content - stays fixed size */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
          {/* Phase icon */}
          {isActive && (
            <div
              className="mb-2 transition-colors duration-300"
              style={{ color: 'white' }}
            >
              {renderPhaseIcon(currentPhaseConfig.icon, 32)}
            </div>
          )}

          {/* Phase label */}
          <span
            className="text-2xl sm:text-3xl font-bold transition-colors duration-300"
            style={{
              color: isActive ? 'white' : '#78716c',
              textShadow: isActive ? '0 2px 10px rgba(0,0,0,0.3)' : 'none'
            }}
          >
            {getPhaseLabel()}
          </span>

          {/* Large countdown */}
          {isActive && (
            <span
              className="text-6xl sm:text-7xl font-light tabular-nums"
              style={{
                color: 'white',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                opacity: 0.95
              }}
            >
              {phaseRemaining}
            </span>
          )}

          {/* Ready state hint */}
          {!isActive && (
            <span className="text-stone-400 mt-2 text-sm">
              {t('breathing.tapToStart') || 'Trykk Start'}
            </span>
          )}
        </div>

        {/* Phase indicator bar at bottom */}
        {isActive && (
          <div className="absolute -bottom-8 left-0 right-0 flex justify-center gap-2">
            {activePhases.map((p, index) => {
              const isCurrentPhase = phase === p;
              const isPastPhase = activePhases.indexOf(phase) > index;
              const config = PHASE_CONFIG[p];

              return (
                <div key={p} className="flex flex-col items-center gap-1">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: isCurrentPhase ? '48px' : '24px',
                      backgroundColor: isCurrentPhase
                        ? config.color
                        : isPastPhase
                          ? config.color + '60'
                          : '#d4d4d4',
                      boxShadow: isCurrentPhase ? `0 0 8px ${config.color}` : 'none',
                    }}
                  />
                  <span
                    className="text-[10px] font-medium transition-colors duration-300"
                    style={{
                      color: isCurrentPhase ? config.color : '#a8a29e',
                      opacity: isCurrentPhase ? 1 : 0.7
                    }}
                  >
                    {language === 'en' ? config.labelEn : config.labelNo}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Timer and cycle info */}
      <div className={`text-center ${isActive ? 'mt-8' : ''} mb-4`}>
        <p className="text-xl font-medium text-stone-600 tabular-nums">
          {formatTime(totalTime)} / {formatTime(sessionDuration)}
        </p>
        <p className="text-stone-400 text-sm">
          {t('breathing.cycles') || 'Sykluser'}: {cycleCount}
        </p>
      </div>

      {/* Main control button */}
      <button
        onClick={isActive ? stopExercise : startExercise}
        className={`px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 mb-6 ${
          isActive
            ? 'bg-stone-600 text-white hover:bg-stone-700'
            : `bg-gradient-to-r ${currentPhaseConfig.gradient} text-white shadow-lg hover:shadow-xl hover:scale-105`
        }`}
      >
        {isActive ? (t('breathing.stop') || 'Stopp') : (t('breathing.start') || 'Start')}
      </button>

      {/* Settings (only when not active) */}
      {!isActive && (
        <div className="w-full max-w-sm space-y-4">
          {/* Pattern selection */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-2 uppercase tracking-wide">
              {t('breathing.pattern') || 'Pustemønster'}
            </label>
            <div className="grid grid-cols-5 gap-1.5">
              {Object.entries(PATTERNS).map(([key, p]) => (
                <button
                  key={key}
                  onClick={() => setSelectedPattern(key)}
                  className={`py-2.5 px-1 rounded-xl text-xs transition-all duration-200 ${
                    selectedPattern === key
                      ? 'bg-gradient-to-br from-accent to-teal-500 text-white shadow-md'
                      : 'bg-white/70 text-stone-600 hover:bg-white hover:shadow'
                  }`}
                >
                  <span className="font-semibold block">{p.name}</span>
                  <span className="opacity-70 text-[9px]">
                    {p.inhale}-{p.holdIn}-{p.exhale}-{p.holdOut}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Sound type selection */}
          <div>
            <label className="block text-xs font-medium text-stone-500 mb-2 uppercase tracking-wide">
              {t('breathing.soundType') || 'Lydtype'}
            </label>
            <div className="grid grid-cols-4 gap-1.5">
              {Object.entries(SOUND_TYPES).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => {
                    setSoundType(config.type);
                    if (config.type !== 'silent') setSoundEnabled(true);
                  }}
                  className={`py-2.5 px-2 rounded-xl text-xs transition-all duration-200 ${
                    soundType === key
                      ? 'bg-gradient-to-br from-accent to-teal-500 text-white shadow-md'
                      : 'bg-white/70 text-stone-600 hover:bg-white hover:shadow'
                  }`}
                >
                  <span className="font-semibold block">{getSoundName(config)}</span>
                  <span className="opacity-70 text-[10px] block truncate">
                    {getSoundDescription(config)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Sound toggle */}
          {soundType !== 'silent' && (
            <div className="flex items-center justify-between p-3 bg-white/60 rounded-xl">
              <span className="text-stone-600 text-sm">
                {t('breathing.soundEnabled') || 'Lyd aktivert'}
              </span>
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`w-12 h-6 rounded-full transition-all duration-200 ${
                  soundEnabled ? 'bg-accent' : 'bg-stone-300'
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    soundEnabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Headphones tip */}
          {soundEnabled && soundType === 'binaural' && (
            <p className="text-xs text-stone-400 text-center flex items-center justify-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 0112.728 0" />
              </svg>
              {t('breathing.headphonesRecommended') || 'Bruk hodetelefoner for binaurale frekvenser'}
            </p>
          )}

          {/* Sound description for harmonic/ocean */}
          {soundEnabled && (soundType === 'harmonic' || soundType === 'ocean') && (
            <p className="text-xs text-stone-400 text-center">
              {soundType === 'harmonic'
                ? (language === 'en'
                    ? 'Sound builds during inhale, releases during exhale'
                    : 'Lyden bygger seg opp ved innpust, løser seg ved utpust')
                : (language === 'en'
                    ? 'Wave sounds synchronized with your breath'
                    : 'Bølgelyder synkronisert med pusten din')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BreathingExercise;
